import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Collapse,
  useTheme,
  useMediaQuery,
  InputBase,
  Tooltip,
  Chip,
  alpha,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  AccountBalance,
  Description,
  SwapHoriz,
  Pix,
  Assessment,
  Settings,
  Person,
  Business,
  ExitToApp,
  Notifications,
  ExpandLess,
  ExpandMore,
  ChevronLeft,
  Group,
  Security,
  Search,
  DarkMode,
  FullscreenOutlined,
  TranslateOutlined,
  KeyboardArrowDown,
  Payment,
  Webhook,
  QrCodeScanner,
  Wallet,
  Send,
  ListAlt,
  Sync,
  CreditCard,
  History,
  BarChart,
  Transform,
  Code,
  Extension,
  AccountBalanceWallet,
  CallReceived,
  QrCode2,
} from '@mui/icons-material';
import { useAppSelector } from '@/hooks/useAppSelector';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { logout } from '@/store/slices/authSlice';
import { selectCurrentUser, selectCurrentCompany } from '@/store/slices/authSlice';
import {
  selectBankAccount,
  selectBankFeatures,
  selectBankProvider,
} from '@/store/slices/bankAccountSlice';
import CompanySelector from '@/components/CompanySelector';
import toast from 'react-hot-toast';

const drawerWidth = 240;

interface MenuItemType {
  text: string;
  icon: React.ReactElement;
  path?: string;
  children?: MenuItemType[];
  permission?: string;
  badge?: string;
}

const MainLayout: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const user = useAppSelector(selectCurrentUser);
  const company = useAppSelector(selectCurrentCompany);
  const bankAccount = useAppSelector(selectBankAccount);
  const features = useAppSelector(selectBankFeatures);
  const provider = useAppSelector(selectBankProvider);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openSubmenu, setOpenSubmenu] = useState<string>('');

  // Sem conta selecionada o slice devolve as features do Sicoob (comportamento histórico) —
  // por isso `provider === null` conta como Sicoob para os itens exclusivos dele.
  const isSicoob = (provider ?? 'sicoob') === 'sicoob';

  /** Mantém só os filhos presentes; grupo sem filhos some do menu */
  const group = (text: string, icon: React.ReactElement, children: (MenuItemType | false)[]): MenuItemType | null => {
    const kids = children.filter((c): c is MenuItemType => Boolean(c));
    return kids.length ? { text, icon, children: kids } : null;
  };

  const menuItems: MenuItemType[] = [
    { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
    { text: 'BI Financeiro', icon: <BarChart />, path: '/bi' },
    group('Conta', <Wallet />, [
      features.saldo && { text: 'Saldo', icon: <AccountBalance />, path: '/conta/saldo' },
      features.extrato && { text: 'Extrato', icon: <ListAlt />, path: '/conta/extrato' },
    ]),
    group('PIX', <Pix />, [
      // Neutros (/bank/*): qualquer provedor
      features.pixRecebidos && { text: 'Recebidos', icon: <CallReceived />, path: '/pix/recebidos' },
      features.pixCobranca && { text: 'Cobrança QR Code', icon: <QrCode2 />, path: '/pix/cobranca' },
      // Sicoob-only (rotas /pix/* antigas)
      isSicoob && { text: 'Enviar', icon: <Send />, path: '/pix/enviar' },
      isSicoob && { text: 'Cobrar (QR Code)', icon: <QrCodeScanner />, path: '/pix/qrcode' },
      isSicoob && { text: 'Cobrança c/ Vencimento', icon: <Description />, path: '/pix/cobv' },
      isSicoob && { text: 'Minhas Chaves', icon: <Pix />, path: '/pix/keys' },
      isSicoob && { text: 'Histórico', icon: <History />, path: '/pix/history' },
      isSicoob && { text: 'Webhooks', icon: <Webhook />, path: '/pix/webhooks' },
    ]),
    features.boletos && group('Boletos', <Description />, [
      { text: 'Emitir', icon: <Description />, path: '/boletos/new' },
      { text: 'Consultar', icon: <ListAlt />, path: '/boletos/list' },
      { text: 'Movimentações', icon: <Sync />, path: '/boletos/movimentacoes' },
      { text: 'Relatórios', icon: <BarChart />, path: '/boletos/reports' },
    ]),
    group('Transferências', <SwapHoriz />, [
      features.ted && { text: 'TED', icon: <Send />, path: '/transfers/ted' },
      features.transferenciaInterna && { text: 'Entre Contas', icon: <SwapHoriz />, path: '/transfers/internal' },
      (features.ted || features.transferenciaInterna) && { text: 'Histórico', icon: <History />, path: '/transfers/history' },
    ]),
    group('Pagar', <Payment />, [
      features.boletos && { text: 'Boleto / Código', icon: <QrCodeScanner />, path: '/pagamentos/pagar' },
      features.dda && { text: 'Boletos DDA', icon: <Description />, path: '/pagamentos/dda' },
      (features.dda || features.boletos) && { text: 'Solicitações', icon: <Payment />, path: '/pagamentos/solicitacoes' },
    ]),
    features.cobrancaLink && { text: 'Vendas por link (cartão)', icon: <CreditCard />, path: '/efi/cobrancas' },
    features.conciliacao && { text: 'Conciliação Efí', icon: <Sync />, path: '/settings/efi' },
    { text: 'Relatórios', icon: <Assessment />, path: '/reports' },
  ].filter((item): item is MenuItemType => Boolean(item));

  const adminMenuItems: MenuItemType[] = [
    { text: 'Empresas', icon: <Business />, path: '/admin/company' },
    { text: 'Usuários', icon: <Group />, path: '/admin/users' },
    { text: 'Contas bancárias', icon: <AccountBalanceWallet />, path: '/settings/contas' },
    { text: 'Integração Sicoob', icon: <AccountBalance />, path: '/settings/sicoob' },
    { text: 'Integração Efí', icon: <AccountBalance />, path: '/settings/efi' },
    { text: 'Webhooks', icon: <Webhook />, path: '/settings/webhook-logs' },
    { text: 'Conversor Certificados', icon: <Transform />, path: '/settings/certificate-converter' },
    { text: 'Apps Integrados', icon: <Extension />, path: '/settings/apps' },
    { text: 'Notificações & Aprovações', icon: <Notifications />, path: '/settings/aprovacoes' },
    { text: 'API & Tokens', icon: <Code />, path: '/settings/api-docs' },
    { text: 'Segurança', icon: <Security />, path: '/admin/security' },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await dispatch(logout()).unwrap();
      navigate('/login');
      toast.success('Logout realizado com sucesso');
    } catch {
      toast.error('Erro ao fazer logout');
    }
  };

  const handleSubmenuClick = (text: string) => {
    setOpenSubmenu(openSubmenu === text ? '' : text);
  };

  const isActive = (path?: string) => {
    return path ? location.pathname === path : false;
  };

  const isParentActive = (item: MenuItemType) => {
    if (item.children) {
      return item.children.some(child => location.pathname === child.path);
    }
    return false;
  };

  const renderMenuItem = (item: MenuItemType, nested = false) => {
    const active = isActive(item.path);
    const parentActive = isParentActive(item);

    if (item.children) {
      return (
        <React.Fragment key={item.text}>
          <ListItemButton
            onClick={() => handleSubmenuClick(item.text)}
            sx={{
              mx: 1,
              my: 0.25,
              borderRadius: 1.5,
              pl: nested ? 4 : 1.5,
              color: parentActive ? 'primary.main' : 'text.secondary',
              backgroundColor: parentActive ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                color: 'primary.main',
              },
            }}
          >
            <ListItemIcon sx={{
              minWidth: 36,
              color: parentActive ? 'primary.main' : 'inherit'
            }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.text}
              primaryTypographyProps={{
                fontSize: '0.875rem',
                fontWeight: parentActive ? 600 : 500,
              }}
            />
            {item.badge && (
              <Chip
                label={item.badge}
                size="small"
                color="primary"
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  mr: 1,
                }}
              />
            )}
            {openSubmenu === item.text ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
          <Collapse in={openSubmenu === item.text} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children.map((child) => renderMenuItem(child, true))}
            </List>
          </Collapse>
        </React.Fragment>
      );
    }

    return (
      <ListItem key={item.text} disablePadding sx={{ px: 1, py: 0.125 }}>
        <ListItemButton
          onClick={() => {
            if (item.path) {
              navigate(item.path);
              if (isMobile) {
                setMobileOpen(false);
              }
            }
          }}
          sx={{
            borderRadius: 1.5,
            pl: nested ? 4 : 1.5,
            py: 0.75,
            color: active ? 'primary.main' : 'text.secondary',
            backgroundColor: active ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
            '&:hover': {
              backgroundColor: active
                ? alpha(theme.palette.primary.main, 0.12)
                : alpha(theme.palette.primary.main, 0.08),
              color: 'primary.main',
            },
          }}
        >
          <ListItemIcon sx={{
            minWidth: 36,
            color: active ? 'primary.main' : 'inherit',
          }}>
            {item.icon}
          </ListItemIcon>
          <ListItemText
            primary={item.text}
            primaryTypographyProps={{
              fontSize: '0.875rem',
              fontWeight: active ? 600 : 500,
            }}
          />
        </ListItemButton>
      </ListItem>
    );
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo Header */}
      <Box
        sx={{
          p: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1.5,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}`,
          }}
        >
          <AccountBalance sx={{ color: 'white', fontSize: 20 }} />
        </Box>
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: '1rem',
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            FalaVipPay
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
            Sistema Financeiro
          </Typography>
        </Box>
        {isMobile && (
          <IconButton onClick={handleDrawerToggle} sx={{ ml: 'auto' }}>
            <ChevronLeft />
          </IconButton>
        )}
      </Box>

      {/* Conta bancária selecionada (empresa · banco) */}
      <Box
        sx={{
          mx: 1.5,
          mb: 0.5,
          px: 1,
          py: 0.5,
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          backgroundColor: alpha(theme.palette.text.primary, 0.04),
          border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
        }}
        title={bankAccount ? `${company?.name || ''} · ${bankAccount.label}` : 'Nenhuma conta bancária selecionada'}
      >
        <AccountBalanceWallet sx={{ fontSize: 14, color: 'text.secondary' }} />
        <Typography
          variant="caption"
          sx={{
            fontSize: '0.7rem',
            color: bankAccount ? 'text.secondary' : 'text.disabled',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {bankAccount
            ? `${bankAccount.providerLabel} · ${bankAccount.accountNumber || bankAccount.label}`
            : 'Sem conta bancária'}
        </Typography>
      </Box>

      {/* Menu Navigation */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', py: 0.5 }}>
        {/* Menu Principal */}
        <Typography
          variant="caption"
          sx={{
            px: 2,
            py: 1,
            display: 'block',
            fontWeight: 600,
            fontSize: '0.65rem',
            letterSpacing: '0.5px',
            color: 'text.disabled',
            textTransform: 'uppercase',
          }}
        >
          Menu Principal
        </Typography>
        <List disablePadding>
          {menuItems.map((item) => renderMenuItem(item))}
        </List>

        {/* Admin Section */}
        <Typography
          variant="caption"
          sx={{
            px: 2,
            py: 1,
            mt: 1,
            display: 'block',
            fontWeight: 600,
            fontSize: '0.65rem',
            letterSpacing: '0.5px',
            color: 'text.disabled',
            textTransform: 'uppercase',
          }}
        >
          Configurações
        </Typography>
        <List disablePadding>
          {adminMenuItems.map((item) => renderMenuItem(item))}
        </List>
      </Box>

      {/* User Profile Card */}
      <Box
        sx={{
          p: 1.5,
          mx: 1,
          mb: 1.5,
          borderRadius: 2,
          backgroundColor: alpha(theme.palette.primary.main, 0.04),
          border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar
            sx={{
              width: 36,
              height: 36,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              fontSize: '0.95rem',
              fontWeight: 600,
            }}
          >
            {user?.name?.charAt(0).toUpperCase() || 'U'}
          </Avatar>
          <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                fontSize: '0.85rem',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {user?.name || 'Usuário'}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: 'block',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontSize: '0.7rem',
              }}
            >
              {user?.email || 'email@exemplo.com'}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={handleLogout}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                color: 'error.main',
                backgroundColor: alpha(theme.palette.error.main, 0.08),
              },
            }}
          >
            <ExitToApp fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* AppBar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          backgroundColor: 'background.paper',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Toolbar sx={{ gap: 2 }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{
              display: { md: 'none' },
              color: 'text.primary',
            }}
          >
            <MenuIcon />
          </IconButton>

          {/* Search Bar */}
          <Box
            sx={{
              display: { xs: 'none', sm: 'flex' },
              alignItems: 'center',
              backgroundColor: alpha(theme.palette.primary.main, 0.04),
              borderRadius: 2,
              px: 2,
              py: 0.5,
              flexGrow: 1,
              maxWidth: 400,
              border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              '&:focus-within': {
                borderColor: theme.palette.primary.main,
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
              },
            }}
          >
            <Search sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} />
            <InputBase
              placeholder="Buscar..."
              sx={{
                flex: 1,
                fontSize: '0.875rem',
                '& input': {
                  py: 0.5,
                },
              }}
            />
            <Typography
              variant="caption"
              sx={{
                px: 1,
                py: 0.25,
                borderRadius: 1,
                backgroundColor: alpha(theme.palette.divider, 0.5),
                color: 'text.secondary',
                fontSize: '0.65rem',
                fontWeight: 600,
              }}
            >
              Ctrl+K
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          {/* Company Selector */}
          <CompanySelector />

          {/* Action Icons */}
          <Tooltip title="Idioma">
            <IconButton sx={{ color: 'text.secondary' }}>
              <TranslateOutlined />
            </IconButton>
          </Tooltip>

          <Tooltip title="Tela cheia">
            <IconButton sx={{ color: 'text.secondary' }}>
              <FullscreenOutlined />
            </IconButton>
          </Tooltip>

          <Tooltip title="Modo escuro">
            <IconButton sx={{ color: 'text.secondary' }}>
              <DarkMode />
            </IconButton>
          </Tooltip>

          <Tooltip title="Notificações">
            <IconButton sx={{ color: 'text.secondary' }}>
              <Badge badgeContent={4} color="error">
                <Notifications />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* Profile Button */}
          <Box
            onClick={handleProfileMenuOpen}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              p: 0.5,
              borderRadius: 2,
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
              },
            }}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                fontSize: '0.85rem',
                fontWeight: 600,
              }}
            >
              {user?.name?.charAt(0).toUpperCase() || 'U'}
            </Avatar>
            <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.2 }}>
                {user?.name || 'Usuário'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                {user?.role === 'admin' ? 'Administrador' : String(user?.role) === 'master' ? 'Master' : 'Usuário'}
              </Typography>
            </Box>
            <KeyboardArrowDown sx={{ color: 'text.secondary', display: { xs: 'none', lg: 'block' } }} />
          </Box>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            PaperProps={{
              elevation: 0,
              sx: {
                mt: 1,
                minWidth: 200,
                overflow: 'visible',
                boxShadow: theme.shadows[8],
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                '&:before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  right: 20,
                  width: 10,
                  height: 10,
                  bgcolor: 'background.paper',
                  transform: 'translateY(-50%) rotate(45deg)',
                  zIndex: 0,
                  borderTop: `1px solid ${theme.palette.divider}`,
                  borderLeft: `1px solid ${theme.palette.divider}`,
                },
              },
            }}
          >
            <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="subtitle2" fontWeight={600}>
                {user?.name || 'Usuário'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user?.email || 'email@exemplo.com'}
              </Typography>
            </Box>
            <MenuItem
              onClick={() => {
                navigate('/profile');
                handleProfileMenuClose();
              }}
              sx={{ py: 1.5 }}
            >
              <ListItemIcon>
                <Person fontSize="small" />
              </ListItemIcon>
              Meu Perfil
            </MenuItem>
            <MenuItem
              onClick={() => {
                navigate('/settings');
                handleProfileMenuClose();
              }}
              sx={{ py: 1.5 }}
            >
              <ListItemIcon>
                <Settings fontSize="small" />
              </ListItemIcon>
              Configurações
            </MenuItem>
            <Box sx={{ borderTop: `1px solid ${theme.palette.divider}`, mt: 1, pt: 1 }}>
              <MenuItem onClick={handleLogout} sx={{ py: 1.5, color: 'error.main' }}>
                <ListItemIcon>
                  <ExitToApp fontSize="small" sx={{ color: 'error.main' }} />
                </ListItemIcon>
                Sair
              </MenuItem>
            </Box>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              border: 'none',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              border: 'none',
              borderRight: `1px solid ${theme.palette.divider}`,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          height: '100vh',
          overflow: 'auto',
          backgroundColor: theme.palette.background.default,
          pt: { xs: 8, md: 9 },
          px: { xs: 2, sm: 3 },
          pb: 3,
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

export default MainLayout;
